import React from 'react';
import { Modal, Breadcrumb, List, Button, Input, Spin, Empty, Typography, message } from 'antd';
import {
    FolderOutlined,
    FileOutlined,
    ArrowUpOutlined,
    HomeOutlined,
    ReloadOutlined,
} from '@ant-design/icons';
import { browseVideoScanPath } from '../../api/video';
import { modalWidth } from '../../utils/responsive';
import { browseImageScanPath } from '../../api/image';
import './ScanPathPicker.css';

const BROWSE_FN = {
    video: browseVideoScanPath,
    image: browseImageScanPath,
};

class ScanPathPicker extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: false,
            browse: null,
            selectedPath: '',
        };
    }

    getBrowseFn = () => {
        const { mediaType = 'video' } = this.props;
        return BROWSE_FN[mediaType] || browseVideoScanPath;
    };

    componentDidUpdate(prevProps) {
        if (this.props.open && !prevProps.open) {
            this.setState({ selectedPath: '' });
            this.loadBrowse(null);
        }
    }

    loadBrowse = async (path) => {
        this.setState({ loading: true });
        try {
            const data = await this.getBrowseFn()(path || undefined);
            const next = { browse: data };
            if (data?.currentPath) {
                next.selectedPath = data.currentPath;
            } else if (data?.atRoots) {
                next.selectedPath = '';
            }
            this.setState(next);
        } catch (e) {
            message.error(e.message || '加载目录失败');
            this.setState({ browse: null });
        } finally {
            this.setState({ loading: false });
        }
    };

    buildBreadcrumbItems = () => {
        const { browse } = this.state;
        const items = [
            {
                title: (
                    <button
                        type="button"
                        className="scan-path-picker__crumb"
                        onClick={() => this.loadBrowse(null)}
                    >
                        <HomeOutlined /> 扫描根目录
                    </button>
                ),
            },
        ];
        if (!browse?.currentPath) {
            return items;
        }
        const parts = browse.currentPath.split('/').filter(Boolean);
        let acc = browse.currentPath.startsWith('/') ? '' : '';
        parts.forEach((part, index) => {
            acc += `/${part}`;
            const path = acc;
            const isLast = index === parts.length - 1;
            items.push({
                title: isLast ? (
                    <span className="scan-path-picker__crumb scan-path-picker__crumb--current">
                        {part}
                    </span>
                ) : (
                    <button
                        type="button"
                        className="scan-path-picker__crumb"
                        onClick={() => this.loadBrowse(path)}
                    >
                        {part}
                    </button>
                ),
            });
        });
        return items;
    };

    enterDirectory = (entry) => {
        if (entry.directory) {
            this.loadBrowse(entry.path);
        } else {
            this.setState({ selectedPath: entry.path });
        }
    };

    goUp = () => {
        const { browse } = this.state;
        if (browse?.parentPath) {
            this.loadBrowse(browse.parentPath);
            return;
        }
        this.loadBrowse(null);
    };

    handleScan = () => {
        const path = this.state.selectedPath.trim();
        if (!path) {
            message.warning('请先选择目录或文件');
            return;
        }
        this.props.onScan(path);
    };

    render() {
        const {
            open,
            title = '选择扫描路径',
            hint = '在服务端白名单目录内浏览并选择要扫描的文件夹或文件',
            scanning = false,
            onCancel,
        } = this.props;
        const { loading, browse, selectedPath } = this.state;

        return (
            <Modal
                className="scan-path-picker"
                title={title}
                open={open}
                onCancel={onCancel}
                width={modalWidth(680)}
                destroyOnClose
                footer={[
                    <Button
                        key="up"
                        icon={<ArrowUpOutlined />}
                        onClick={this.goUp}
                        disabled={browse?.atRoots}
                    >
                        上级
                    </Button>,
                    <Button
                        key="refresh"
                        icon={<ReloadOutlined />}
                        onClick={() => this.loadBrowse(browse?.currentPath || null)}
                    >
                        刷新
                    </Button>,
                    <Button key="cancel" onClick={onCancel}>
                        取消
                    </Button>,
                    <Button key="scan" type="primary" loading={scanning} onClick={this.handleScan}>
                        扫描选中路径
                    </Button>,
                ]}
            >
                <Typography.Paragraph type="secondary" className="scan-path-picker__hint">
                    {hint}
                </Typography.Paragraph>

                <Breadcrumb className="scan-path-picker__breadcrumb" items={this.buildBreadcrumbItems()} />

                <div className="scan-path-picker__list-wrap">
                    <Spin spinning={loading}>
                        {!loading && (!browse?.entries || browse.entries.length === 0) ? (
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={
                                    browse?.atRoots
                                        ? '未配置可用的扫描根目录，请检查 application.yml'
                                        : '当前目录下没有子文件夹或可扫描文件'
                                }
                            />
                        ) : (
                            <List
                                className="scan-path-picker__list"
                                size="small"
                                dataSource={browse?.entries || []}
                                renderItem={(entry) => (
                                    <List.Item
                                        className={
                                            selectedPath === entry.path
                                                ? 'scan-path-picker__item scan-path-picker__item--selected'
                                                : 'scan-path-picker__item'
                                        }
                                        onClick={() => this.setState({ selectedPath: entry.path })}
                                        onDoubleClick={() => entry.directory && this.enterDirectory(entry)}
                                        actions={
                                            entry.directory
                                                ? [
                                                      <Button
                                                          key="enter"
                                                          type="link"
                                                          size="small"
                                                          onClick={(e) => {
                                                              e.stopPropagation();
                                                              this.enterDirectory(entry);
                                                          }}
                                                      >
                                                          进入
                                                      </Button>,
                                                  ]
                                                : undefined
                                        }
                                    >
                                        <List.Item.Meta
                                            avatar={
                                                entry.directory ? (
                                                    <FolderOutlined className="scan-path-picker__icon scan-path-picker__icon--dir" />
                                                ) : (
                                                    <FileOutlined className="scan-path-picker__icon" />
                                                )
                                            }
                                            title={entry.name}
                                            description={
                                                entry.directory ? '文件夹' : '文件（可扫描单个）'
                                            }
                                        />
                                    </List.Item>
                                )}
                            />
                        )}
                    </Spin>
                </div>

                <div className="scan-path-picker__selected">
                    <span className="scan-path-picker__selected-label">选中路径</span>
                    <Input
                        value={selectedPath}
                        onChange={(e) => this.setState({ selectedPath: e.target.value })}
                        placeholder="点击上方目录选择，也可手动输入绝对路径"
                        onPressEnter={this.handleScan}
                    />
                </div>
            </Modal>
        );
    }
}

export default ScanPathPicker;
